<template>
  
  <div class="fixed inset-0 overflow-y-auto z-50">
    <!-- Background overlay with fade effect -->
    <transition name="fade">
      <div v-if="show" class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="$emit('close')"></div>
    </transition>

    <!-- Modal container -->
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Modal content with scale and fade transition -->
      <transition name="modal">
        <div v-if="show" class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <!-- Header -->
            <div class="flex justify-between items-start">
              <div>
                <h3 class="text-2xl leading-6 font-bold text-[#035a76]">
                  {{ staff.name }}
                </h3>
                <p class="text-lg text-gray-600">{{ staff.position }}</p>
              </div>
              <button @click="$emit('close')" class="text-gray-400 hover:text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Tabs -->
            <div class="mt-6 border-b border-gray-200">
              <nav class="-mb-px flex space-x-8 overflow-x-auto pb-2">
                <button
                  v-for="tab in enhancedTabs"
                  :key="tab.id"
                  @click="activeTab = tab.id"
                  :class="[
                    activeTab === tab.id 
                      ? 'border-[#035a76] text-[#035a76]' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm min-w-max'
                  ]"
                >
                  {{ tab.name }}
                  <span v-if="tab.count !== undefined" class="ml-1 bg-[#035a76] text-white text-xs px-2 py-0.5 rounded-full">
                    {{ tab.count }}
                  </span>
                </button>
              </nav>
            </div>

            <!-- Tab Content with fade transition -->
            <transition name="fade" mode="out-in">
              <div class="mt-6" :key="activeTab">
                <!-- Basic Information Tab -->
                <div v-if="activeTab === 'basic'">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <!-- Personal Details -->
                    <div class="col-span-1">
                      <div class="bg-gray-50 p-4 rounded-md">
                        <h4 class="text-sm font-medium text-gray-500 mb-3">PERSONAL DETAILS</h4>
                        <div class="space-y-3">
                          <div v-for="(value, key) in personalDetails" :key="key">
                            <p class="text-xs text-gray-500 capitalize">{{ key.replace(/([A-Z])/g, ' $1') }}</p>
                            <p class="text-sm">{{ value || 'N/A' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Employment Details -->
                    <div class="col-span-1">
                      <div class="bg-gray-50 p-4 rounded-md">
                        <h4 class="text-sm font-medium text-gray-500 mb-3">EMPLOYMENT DETAILS</h4>
                        <div class="space-y-3">
                          <div>
                            <p class="text-xs text-gray-500">Staff ID</p>
                            <p class="text-sm">{{ staff.id }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Position</p>
                            <p class="text-sm">{{ staff.position }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Department</p>
                            <p class="text-sm">{{ staff.department || 'N/A' }}</p>
                          </div>
                          <div v-if="staff.section">
                            <p class="text-xs text-gray-500">Section/Class</p>
                            <p class="text-sm">{{ staff.section }} <span v-if="staff.class">({{ staff.class }})</span></p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Joining Date</p>
                            <p class="text-sm">{{ formatDate(staff.joiningDate) }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Status</p>
                            <p class="text-sm">{{ staff.status }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Contact Information -->
                    <div class="col-span-1">
                      <div class="bg-gray-50 p-4 rounded-md">
                        <h4 class="text-sm font-medium text-gray-500 mb-3">CONTACT INFORMATION</h4>
                        <div class="space-y-3">
                          <div>
                            <p class="text-xs text-gray-500">Email</p>
                            <p class="text-sm">{{ staff.email }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Phone</p>
                            <p class="text-sm">{{ staff.phone || 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Address</p>
                            <p class="text-sm">{{ staff.address || 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Emergency Contact</p>
                            <p class="text-sm">{{ staff.emergencyContact || 'N/A' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Onboarding Tab -->
                <div v-if="activeTab === 'onboarding'">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Education History -->
                    <div>
                      <div class="bg-gray-50 p-4 rounded-md">
                        <h4 class="text-sm font-medium text-gray-500 mb-3">EDUCATION HISTORY</h4>
                        <div v-for="(edu, index) in staff.onboardingData.education" :key="index" class="mb-4 pb-4 border-b border-gray-200 last:border-0">
                          <p class="font-medium text-sm">{{ edu.certificate }}</p>
                          <p class="text-gray-500 text-sm">{{ edu.school }}</p>
                          <p class="text-gray-500 text-xs mt-1">{{ edu.startYear }} - {{ edu.endYear }}</p>
                          <p v-if="edu.specialization" class="text-gray-500 text-xs">Specialization: {{ edu.specialization }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Work Experience -->
                    <div>
                      <div class="bg-gray-50 p-4 rounded-md">
                        <h4 class="text-sm font-medium text-gray-500 mb-3">WORK EXPERIENCE</h4>
                        <div v-for="(job, index) in staff.onboardingData.workHistory" :key="index" class="mb-4 pb-4 border-b border-gray-200 last:border-0">
                          <p class="font-medium text-sm">{{ job.position }}</p>
                          <p class="text-gray-500 text-sm">{{ job.company }}</p>
                          <p class="text-gray-500 text-xs mt-1">{{ formatDate(job.startDate) }} - {{ job.endDate ? formatDate(job.endDate) : 'Present' }}</p>
                          <p v-if="job.responsibilities" class="text-gray-500 text-xs mt-1">Responsibilities: {{ job.responsibilities }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Documents Tab -->
                <div v-if="activeTab === 'documents'">
                  <div class="bg-gray-50 p-4 rounded-md">
                    <h4 class="text-sm font-medium text-gray-500 mb-3">DOCUMENTS</h4>
                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div v-for="(doc, index) in staff.onboardingData.documents" :key="index" class="border rounded-md p-3 hover:bg-gray-100">
                        <div class="flex items-center">
                          <div class="p-2 rounded-full bg-blue-100 text-blue-600 mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <p class="font-medium text-sm capitalize">{{ doc.type }}</p>
                            <p class="text-gray-500 text-xs">{{ doc.uploadDate ? formatDate(doc.uploadDate) : 'Uploaded during onboarding' }}</p>
                            <button @click="viewDocument(doc)" class="text-blue-600 text-xs mt-1 hover:text-blue-800">
                              View Document
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Contract Tab -->
                <div v-if="activeTab === 'contract'">
                  <div class="bg-gray-50 p-4 rounded-md">
                    <h4 class="text-sm font-medium text-gray-500 mb-3">CONTRACT DETAILS</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div class="space-y-3">
                          <div>
                            <p class="text-xs text-gray-500">Contract Type</p>
                            <p class="text-sm">{{ staff.contract?.type || 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Start Date</p>
                            <p class="text-sm">{{ staff.contract?.startDate ? formatDate(staff.contract.startDate) : 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">End Date</p>
                            <p class="text-sm">{{ staff.contract?.endDate ? formatDate(staff.contract.endDate) : 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Duration</p>
                            <p class="text-sm">{{ staff.contract?.duration || 'N/A' }} months</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div class="space-y-3">
                          <div>
                            <p class="text-xs text-gray-500">Salary</p>
                            <p class="text-sm">{{ staff.contract?.salary ? `GHS ${staff.contract.salary}` : 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Payment Frequency</p>
                            <p class="text-sm">{{ staff.contract?.paymentFrequency || 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">SSNIT Number</p>
                            <p class="text-sm">{{ staff.contract?.ssnitNumber || 'N/A' }}</p>
                          </div>
                          <div>
                            <p class="text-xs text-gray-500">Bank Details</p>
                            <p class="text-sm">{{ staff.contract?.bankDetails || 'N/A' }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Leaves Tab -->
                <div v-if="activeTab === 'leaves'">
                  <div class="bg-gray-50 p-4 rounded-md">
                    <div class="flex justify-between items-center mb-4">
                      <h4 class="text-sm font-medium text-gray-500">LEAVE HISTORY</h4>
                      <div class="flex space-x-2">
                        <select v-model="leaveYearFilter" class="text-sm border rounded px-2 py-1">
                          <option value="all">All Years</option>
                          <option v-for="year in availableYears" :value="year">{{ year }}</option>
                        </select>
                        <select v-model="leaveStatusFilter" class="text-sm border rounded px-2 py-1">
                          <option value="all">All Statuses</option>
                          <option value="approved">Approved</option>
                          <option value="pending">Pending</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="overflow-x-auto">
                      <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-100">
                          <tr>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          <tr v-for="leave in filteredLeaves" :key="leave.id">
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ leave.type }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ formatDate(leave.startDate) }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ formatDate(leave.endDate) }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ leave.days }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">
                              <span :class="{
                                'bg-green-100 text-green-800': leave.status === 'approved',
                                'bg-yellow-100 text-yellow-800': leave.status === 'pending',
                                'bg-red-100 text-red-800': leave.status === 'rejected'
                              }" class="px-2 py-1 rounded-full text-xs">
                                {{ leave.status }}
                              </span>
                            </td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">
                              <button @click="viewLeaveDetails(leave)" class="text-blue-600 hover:text-blue-800 text-xs">
                                View
                              </button>
                            </td>
                          </tr>
                          <tr v-if="filteredLeaves.length === 0">
                            <td colspan="6" class="px-4 py-4 text-center text-sm text-gray-500">
                              No leave records found
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- Loans Tab -->
                <div v-if="activeTab === 'loans'">
                  <div class="bg-gray-50 p-4 rounded-md">
                    <div class="flex justify-between items-center mb-4">
                      <h4 class="text-sm font-medium text-gray-500">LOAN HISTORY</h4>
                      <div class="flex space-x-2">
                        <select v-model="loanStatusFilter" class="text-sm border rounded px-2 py-1">
                          <option value="all">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="paid">Paid</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="overflow-x-auto">
                      <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-100">
                          <tr>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Loan Type</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          <tr v-for="loan in filteredLoans" :key="loan.id">
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ loan.type }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">GHS {{ loan.amount.toLocaleString() }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ formatDate(loan.date) }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">
                              <span :class="{
                                'bg-green-100 text-green-800': loan.status === 'paid',
                                'bg-yellow-100 text-yellow-800': loan.status === 'pending',
                                'bg-blue-100 text-blue-800': loan.status === 'active'
                              }" class="px-2 py-1 rounded-full text-xs">
                                {{ loan.status }}
                              </span>
                            </td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">
                              {{ loan.status === 'paid' ? '0.00' : `GHS ${loan.balance.toLocaleString()}` }}
                            </td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">
                              <button @click="viewLoanDetails(loan)" class="text-blue-600 hover:text-blue-800 text-xs">
                                View
                              </button>
                              <button v-if="loan.status === 'active'" @click="makeLoanPayment(loan)" class="ml-2 text-green-600 hover:text-green-800 text-xs">
                                Pay
                              </button>
                            </td>
                          </tr>
                          <tr v-if="filteredLoans.length === 0">
                            <td colspan="6" class="px-4 py-4 text-center text-sm text-gray-500">
                              No loan records found
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- Performance Tab -->
                <div v-if="activeTab === 'performance'">
                  <div class="bg-gray-50 p-4 rounded-md">
                    <div class="flex justify-between items-center mb-4">
                      <h4 class="text-sm font-medium text-gray-500">PERFORMANCE REVIEWS</h4>
                      <div class="flex space-x-2">
                        <select v-model="performanceYearFilter" class="text-sm border rounded px-2 py-1">
                          <option value="all">All Years</option>
                          <option v-for="year in availablePerformanceYears" :value="year">{{ year }}</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="space-y-4">
                      <div v-for="review in filteredPerformanceReviews" :key="review.id" class="border rounded-md p-4 hover:bg-gray-50">
                        <div class="flex justify-between items-start">
                          <div>
                            <h5 class="font-medium text-sm">{{ review.title }}</h5>
                            <p class="text-gray-500 text-xs">{{ formatDate(review.date) }} • Conducted by {{ review.reviewer }}</p>
                          </div>
                          <div class="flex items-center">
                            <span :class="{
                              'bg-green-100 text-green-800': review.rating >= 4,
                              'bg-yellow-100 text-yellow-800': review.rating >= 2.5 && review.rating < 4,
                              'bg-red-100 text-red-800': review.rating < 2.5
                            }" class="px-2 py-1 rounded-full text-xs font-medium">
                              {{ review.rating.toFixed(1) }}/5.0
                            </span>
                          </div>
                        </div>
                        <div class="mt-2">
                          <p class="text-sm text-gray-700 line-clamp-2">{{ review.comments }}</p>
                        </div>
                        <div class="mt-2 flex justify-end">
                          <button @click="viewPerformanceDetails(review)" class="text-blue-600 hover:text-blue-800 text-xs">
                            View Full Review
                          </button>
                        </div>
                      </div>
                      
                      <div v-if="filteredPerformanceReviews.length === 0" class="text-center py-4 text-sm text-gray-500">
                        No performance reviews found
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Attendance Tab -->
                <div v-if="activeTab === 'attendance'">
                  <div class="bg-gray-50 p-4 rounded-md">
                    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                      <h4 class="text-sm font-medium text-gray-500">ATTENDANCE RECORDS</h4>
                      <div class="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                        <select v-model="attendanceYearFilter" class="text-sm border rounded px-2 py-1">
                          <option v-for="year in availableAttendanceYears" :value="year">{{ year }}</option>
                        </select>
                        <select v-model="attendanceMonthFilter" class="text-sm border rounded px-2 py-1">
                          <option value="all">All Months</option>
                          <option v-for="(month, index) in months" :value="index + 1">{{ month }}</option>
                        </select>
                        <button @click="exportAttendance" class="text-sm border rounded px-2 py-1 bg-[#035a76] text-white hover:bg-[#02485e]">
                          Export
                        </button>
                      </div>
                    </div>
                    
                    <div class="overflow-x-auto">
                      <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-100">
                          <tr>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Check In</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Check Out</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          <tr v-for="record in filteredAttendance" :key="record.date">
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ formatDate(record.date) }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ record.checkIn || '--:--' }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ record.checkOut || '--:--' }}</td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">
                              <span :class="{
                                'bg-green-100 text-green-800': record.status === 'present',
                                'bg-yellow-100 text-yellow-800': record.status === 'late',
                                'bg-red-100 text-red-800': record.status === 'absent',
                                'bg-blue-100 text-blue-800': record.status === 'leave'
                              }" class="px-2 py-1 rounded-full text-xs">
                                {{ record.status }}
                              </span>
                            </td>
                            <td class="px-4 py-2 whitespace-nowrap text-sm">{{ record.hours || '--' }}</td>
                            <td class="px-4 py-2 text-sm max-w-xs truncate">{{ record.notes || '-' }}</td>
                          </tr>
                          <tr v-if="filteredAttendance.length === 0">
                            <td colspan="6" class="px-4 py-4 text-center text-sm text-gray-500">
                              No attendance records found for selected period
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <div class="mt-4 flex justify-between items-center">
                      <div class="text-sm text-gray-500">
                        Showing {{ filteredAttendance.length }} records
                      </div>
                      <div class="flex space-x-2">
                        <button 
                          @click="attendancePage > 1 ? attendancePage-- : null" 
                          :disabled="attendancePage === 1"
                          class="px-3 py-1 border rounded text-sm disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button 
                          @click="attendancePage++" 
                          :disabled="filteredAttendance.length < attendancePageSize"
                          class="px-3 py-1 border rounded text-sm disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              type="button" 
              @click="$emit('close')" 
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';


const props = defineProps({
  staff: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && value.name && value.position;
    }
  },
  show: {
    type: Boolean,
    default: true
  }
});


const emit = defineEmits(['close', 'view-document', 'view-leave', 'view-loan', 'view-performance', 'export-attendance']);

// Enhanced tabs with counts
const enhancedTabs = computed(() => [
  { id: 'basic', name: 'Basic Info' },
  { id: 'onboarding', name: 'Onboarding' },
  { id: 'documents', name: 'Documents', count: props.staff.onboardingData?.documents?.length || 0 },
  { id: 'contract', name: 'Contract' },
  { id: 'leaves', name: 'Leaves', count: props.staff.leaves?.length || 0 },
  { id: 'loans', name: 'Loans', count: props.staff.loans?.length || 0 },
  { id: 'performance', name: 'Performance', count: props.staff.performanceReviews?.length || 0 },
  { id: 'attendance', name: 'Attendance' }
]);

const activeTab = ref('basic');

// Leaves tab filters
const leaveYearFilter = ref('all');
const leaveStatusFilter = ref('all');

// Loans tab filters
const loanStatusFilter = ref('all');

// Performance tab filters
const performanceYearFilter = ref('all');

// Attendance tab filters
const attendanceYearFilter = ref(new Date().getFullYear());
const attendanceMonthFilter = ref('all');
const attendancePage = ref(1);
const attendancePageSize = 20;

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Personal details computed property
const personalDetails = computed(() => ({
  fullName: props.staff.name,
  dateOfBirth: props.staff.onboardingData?.personalInfo?.dob ? formatDate(props.staff.onboardingData.personalInfo.dob) : 'N/A',
  gender: props.staff.onboardingData?.personalInfo?.gender || 'N/A',
  nationality: props.staff.onboardingData?.personalInfo?.nationality || 'N/A',
  ghanaCardNumber: props.staff.onboardingData?.security?.ghanaCardNumber || 'N/A',
  maritalStatus: props.staff.onboardingData?.marital?.status || 'N/A'
}));

// Computed properties for filtered data
const filteredLeaves = computed(() => {
  let leaves = props.staff.leaves || [];
  
  // Apply year filter
  if (leaveYearFilter.value !== 'all') {
    leaves = leaves.filter(leave => 
      new Date(leave.startDate).getFullYear() == leaveYearFilter.value ||
      new Date(leave.endDate).getFullYear() == leaveYearFilter.value
    );
  }
  
  // Apply status filter
  if (leaveStatusFilter.value !== 'all') {
    leaves = leaves.filter(leave => leave.status === leaveStatusFilter.value);
  }
  
  return leaves.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
});

const filteredLoans = computed(() => {
  let loans = props.staff.loans || [];
  
  if (loanStatusFilter.value !== 'all') {
    loans = loans.filter(loan => loan.status === loanStatusFilter.value);
  }
  
  return loans.sort((a, b) => new Date(b.date) - new Date(a.date));
});

const filteredPerformanceReviews = computed(() => {
  let reviews = props.staff.performanceReviews || [];
  
  if (performanceYearFilter.value !== 'all') {
    reviews = reviews.filter(review => 
      new Date(review.date).getFullYear() == performanceYearFilter.value
    );
  }
  
  return reviews.sort((a, b) => new Date(b.date) - new Date(a.date));
});

const filteredAttendance = computed(() => {
  let records = props.staff.attendance || [];
  
  // Filter by year
  records = records.filter(record => 
    new Date(record.date).getFullYear() == attendanceYearFilter.value
  );
  
  // Filter by month if not 'all'
  if (attendanceMonthFilter.value !== 'all') {
    records = records.filter(record => 
      new Date(record.date).getMonth() + 1 == attendanceMonthFilter.value
    );
  }
  
  // Apply pagination
  const start = (attendancePage.value - 1) * attendancePageSize;
  return records
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(start, start + attendancePageSize);
});

// Available years for filters
const availableYears = computed(() => {
  const years = new Set();
  (props.staff.leaves || []).forEach(leave => {
    years.add(new Date(leave.startDate).getFullYear());
    years.add(new Date(leave.endDate).getFullYear());
  });
  return Array.from(years).sort((a, b) => b - a);
});

const availablePerformanceYears = computed(() => {
  const years = new Set();
  (props.staff.performanceReviews || []).forEach(review => {
    years.add(new Date(review.date).getFullYear());
  });
  return Array.from(years).sort((a, b) => b - a);
});

const availableAttendanceYears = computed(() => {
  const years = new Set();
  (props.staff.attendance || []).forEach(record => {
    years.add(new Date(record.date).getFullYear());
  });
  return Array.from(years).sort((a, b) => b - a);
});

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  } catch (e) {
    console.error('Invalid date format', e);
    return 'Invalid date';
  }
};

// Action handlers
const viewDocument = (doc) => {
  emit('view-document', doc);
};

const viewLeaveDetails = (leave) => {
  emit('view-leave', leave);
};

const viewLoanDetails = (loan) => {
  emit('view-loan', loan);
};

const makeLoanPayment = (loan) => {
  // In a real app, this would open a payment modal
  console.log('Make payment for loan:', loan.id);
};

const viewPerformanceDetails = (review) => {
  emit('view-performance', review);
};

const exportAttendance = () => {
  const filters = {
    year: attendanceYearFilter.value,
    month: attendanceMonthFilter.value
  };
  emit('export-attendance', filters);
};

// Handle escape key to close modal
const onKeyDown = (e) => {
  if (e.key === 'Escape') {
    emit('close');
  }
};

// Add/remove event listener when component mounts/unmounts
onMounted(() => {
  window.addEventListener('keydown', onKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown);
});

// Reset pagination when filters change
watch([attendanceYearFilter, attendanceMonthFilter], () => {
  attendancePage.value = 1;
});
</script>

<style scoped>
/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Tab content fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>